import * as d3 from 'd3'
import _ from 'underscore'
import moment from 'moment'

import dateFormatter from '../helper/dateFormatter'
import { timeScaleWeighted } from '../helper/d3.weightedTimeScale'
import { formatMilliseconds } from '../helper/milliseconds'

let Chart = function() {
    var svg = null

    var dimensions = {
        width: 0,
        height: 0,
    }

    var margin = {
        top: 15,
        right: 40,
        bottom: 15,
        left: 40,
    }

    var eventFlag = {
        width: 14,
        height: 14,
    }

    var currentLabel = '95% Percentile'

    var lines = null

    var scales = null

    let callbackTimestampSelected

    let drawXAxisLabels = true

    var eventMap = {
        cron: { icon: '\uf017', label: 'Cronjob' },
        release: { icon: '\uf007', label: 'New Release' },
        error: { icon: '\uf188', label: 'New Error' },
        maintenance: { icon: '\uf0a0', label: 'Maintenance Started' },
        response: { icon: '\uf012', label: 'Increased Response Times' },
        errorrate: { icon: '\uf188', label: 'Increased Failure Rate' },
        anomaly: { icon: '\uf012', label: 'Anomaly Detected' },
        down: { icon: '\uf063', label: 'Site Down' },
        marker: { icon: '\uf007', label: 'Marker' },
    }

    const eventGroupLimit = 10

    const eventOrder = ['release', 'error', 'response', 'errorrate', 'anomaly', 'maintenance', 'cron', 'down']

    this.setResponseTypeLabel = function(label, label2) {
        // this is quite ugly with the two labels, but its necessary because we
        // are using one parameter for deciding about two outputs.
        currentLabel = label2 || label

        if (svg) {
            let root = d3.select(svg)
            root.selectAll('.y')
                .selectAll('.label')
                .text(currentLabel)
        }
    }

    this.highlight = function(timeIndex, timeIndexNext) {
        let highlight = d3.select(svg).select('.highlight')

        highlight.style('display', 'none')

        if (!timeIndex || !timeIndexNext) {
            return
        }

        highlight
            .attr('x', scales.x(timeIndex))
            .attr('y', 0)
            .attr('width', scales.x(timeIndexNext) - scales.x(timeIndex))
            .attr('height', dimensions.height - margin.top - margin.bottom)
            .style('display', null)
    }

    this.create = function(rootElement, width, height, onTimestampSelected) {
        svg = rootElement
        dimensions.width = width
        dimensions.height = height
        callbackTimestampSelected = onTimestampSelected || function() {}

        let d3Svg = d3
            .select(svg)
            .attr('class', 'chart')
            .attr('width', dimensions.width)
            .attr('height', dimensions.height)

        let container = d3Svg.append('g').attr('class', 'container')

        _initAxis(container)

        let lines = container.append('g').attr('class', 'lines')

        lines
            .append('rect')
            .attr('class', 'background')
            .attr('width', dimensions.width - margin.left - margin.right)
            .attr('height', dimensions.height - margin.top - margin.bottom)

        lines
            .append('rect')
            .attr('class', 'highlight')
            .attr('style', { display: 'none' })

        container.append('g').attr('class', 'rangeMarkers')

        container.append('g').attr('class', 'events')

        container.append('g').attr('class', 'targetMarkers')

        _drawEventInfoBox(container)

        _registerEventHandlers(lines)
    }

    var _initAxis = function(root) {
        let axis = root.append('g').attr('class', 'axis')

        axis.append('g').attr('class', 'x')
        let y = axis.append('g').attr('class', 'y')
        y.append('text')
            .attr('class', 'label')
            .attr('text-anchor', 'start')
            .attr('x', -40)
            .attr('y', -5)
            .text(currentLabel)

        let y2 = axis.append('g').attr('class', 'y2')
        y2.append('text')
            .attr('class', 'label')
            .attr('text-anchor', 'end')
            .attr('x', 20)
            .attr('y', -5)
            .text('Requests')
    }

    var _registerEventHandlers = function(root) {
        let highlight = d3.select(svg).select('.highlight')

        root.on('mousemove', function() {
            let [dataSet, nextDataSet] = _getSelectedItem(d3.mouse(this))

            if (dataSet) {
                const x = scales.x(dataSet.date)
                const width = scales.x(nextDataSet.date) - scales.x(dataSet.date)
                const y = d3.event.clientY

                callbackTimestampSelected(dataSet ? dataSet.dateUtc : null, nextDataSet ? nextDataSet.dateUtc : null, {
                    x: x + margin.left,
                    width,
                    y,
                })

                highlight
                    .attr('x', x)
                    .attr('y', 0)
                    .attr('width', width)
                    .attr('height', dimensions.height - margin.top - margin.bottom)
                    .style('display', null)
            }
        })
        // mouseleave/mouseover should be registered on root element, not lines
        d3.select(svg)
            .on('mouseleave', function() {
                callbackTimestampSelected(null, null, null)
            })
            .on('mouseover', function() {
                highlight.style('display', null)
            })

        let start = [null, null]
        let selection = [null, null]
        let dragBehavior = d3
            .drag()
            .on('start', function() {
                let [dataSet, nextDataSet] = _getSelectedItem(d3.mouse(this))

                if (!dataSet) {
                    return
                }

                start[0] = selection[0] = dataSet
                start[1] = selection[1] = nextDataSet

                highlight
                    .attr('x', scales.x(selection[0].date))
                    .attr('y', 0)
                    .attr('width', scales.x(selection[1].date) - scales.x(selection[0].date))
                    .attr('height', dimensions.height - margin.top - margin.bottom)
                    .style('display', null)
            })
            .on('drag', function() {
                let [dataSet, nextDataSet] = _getSelectedItem(d3.mouse(this))

                if (!dataSet) {
                    return
                }

                if (nextDataSet.date > start[0].date) {
                    selection[1] = nextDataSet
                }

                if (dataSet.date < start[1].date) {
                    selection[0] = dataSet
                }

                highlight
                    .attr('x', scales.x(selection[0].date))
                    .attr('y', 0)
                    .attr('width', scales.x(selection[1].date) - scales.x(selection[0].date))
                    .attr('height', dimensions.height - margin.top - margin.bottom)
                    .style('display', null)
            })
            .on('end', function() {
                let [dataSet, nextDataSet] = _getSelectedItem(d3.mouse(this))

                if (!dataSet) {
                    return
                }

                if (nextDataSet.date > start[0].date) {
                    selection[1] = nextDataSet
                }

                if (dataSet.date < start[1].date) {
                    selection[0] = dataSet
                }

                jQuery(svg).trigger('rangeSelected', selection)
            })

        root.call(dragBehavior)
    }

    var _getSelectedItem = function(mouse) {
        if (!scales) {
            return [null, null]
        }

        let bisect = d3.bisector(function(d) {
            return d.date
        }).right

        let x0 = scales.x.invert(mouse[0])
        let dateItem = Math.min(bisect(lines[0].data, x0), lines[0].data.length - 1)
        let dataSet = lines[0].data[dateItem - 1]
        let nextDataSet = lines[0].data[dateItem]

        return [dataSet, nextDataSet]
    }

    var _scales = function(lines, yAxisMax) {
        return {
            x: d3
                .scaleTime()
                .domain([_.min(_.pluck(lines[1].data, 'date')), _.max(_.pluck(lines[1].data, 'date'))])
                .rangeRound([0, dimensions.width - margin.left - margin.right]),
            response: d3
                .scaleLinear()
                .domain([yAxisMax ? yAxisMax.response : Math.max(_.max(_.pluck(lines[1].data, 'value')), 1), 0])
                .range([0, dimensions.height - margin.top - margin.bottom]),
            requests: d3
                .scaleLinear()
                .domain([yAxisMax ? yAxisMax.requests : Math.max(_.max(_.pluck(lines[0].data, 'value')), 1), 0])
                .range([0, dimensions.height - margin.top - margin.bottom]),
        }
    }

    var _axis = function(scales) {
        return {
            x: d3
                .axisBottom(scales.x)
                .tickSizeInner(-(dimensions.height - margin.top - margin.bottom))
                .tickSizeOuter(3)
                .tickPadding(5)
                .ticks(10)
                .tickFormat(dateFormatter),
            y: d3
                .axisLeft(scales.response)
                .tickSizeInner(-(dimensions.width - margin.left - margin.right))
                .tickSizeOuter(3)
                .tickPadding(5)
                .ticks(5),
            y2: d3
                .axisRight(scales.requests)
                .tickSizeInner(-(dimensions.width - margin.left - margin.right))
                .tickSizeOuter(3)
                .tickPadding(5)
                .ticks(5),
        }
    }

    var _drawAxis = function(scales, axis) {
        var root = d3.select(svg).select('.axis')
        root.attr('width', dimensions.width - margin.left - margin.right)
            .attr('height', dimensions.height - margin.top - margin.bottom)
            .attr('transform', 'translate(' + margin.left + ', ' + margin.top + ')')

        root.selectAll('.x')
            .call(axis.x)
            .attr('transform', 'translate(0, ' + (dimensions.height - margin.bottom - margin.top) + ')')

        root.selectAll('.y').call(axis.y)

        root.selectAll('.y2')
            .call(axis.y2)
            .attr('transform', 'translate(' + (dimensions.width - margin.left - margin.right) + ', 0)')

        if (!drawXAxisLabels) {
            d3.select(svg)
                .selectAll('.axis .x .tick text')
                .remove()
        } else {
            d3.select(svg)
                .selectAll('.axis .x .tick text')
                .attr('class', d => timeScaleWeighted(d, axis.x.scale().domain()))
        }
    }

    var _drawRangeMarkers = function(scales, rangeMarkers) {
        var root = d3.select(svg).select('.rangeMarkers')
        root.attr('width', dimensions.width - margin.left - margin.right)
            .attr('height', dimensions.height - margin.top - margin.bottom)
            .attr('transform', 'translate(' + margin.left + ', ' + margin.top + ')')

        let marker = root.selectAll('g.rangeMarker').data(rangeMarkers)

        // New data
        let newevent = marker.enter()
        let group = newevent.append('g').attr('class', function(marker) {
            return 'rangeMarker ' + marker.type
        })

        group
            .append('rect')
            .attr('class', 'bg')
            .attr('y', 0)
            .attr('height', dimensions.height - margin.top - margin.bottom)
            .on('mousemove', function() {
                let [dataSet, nextDataSet] = _getSelectedItem(d3.mouse(this))

                if (dataSet) {
                    const x = scales.x(dataSet.date)
                    const width = scales.x(nextDataSet.date) - scales.x(dataSet.date)
                    const y = d3.event.clientY

                    callbackTimestampSelected(dataSet ? dataSet.dateUtc : null, nextDataSet ? nextDataSet.dateUtc : null, {
                        x: x + margin.left,
                        width,
                        y,
                    })

                    d3.select(svg)
                        .select('.highlight')
                        .attr('x', x)
                        .attr('y', 0)
                        .attr('width', width)
                        .attr('height', dimensions.height - margin.top - margin.bottom)
                        .style('display', null)
                }
            })

        let button = group
            .append('g')
            .attr('class', 'button')
            .attr('width', 17)
            .attr('height', 17)

        button.on('click', function() {
            d3.event.stopPropagation()
            jQuery(svg).trigger('rangeDelesect')
        })

        button
            .append('rect')
            .attr('class', 'shadow')
            .attr('width', 16)
            .attr('height', 16)
            .attr('x', 1)
            .attr('y', 1)

        button
            .append('rect')
            .attr('class', 'bg')
            .attr('width', 16)
            .attr('height', 16)
            .attr('x', 0)
            .attr('y', 0)

        button
            .append('text')
            .attr('class', 'button icon')
            .attr('x', 3)
            .attr('y', 13)
            .text('\uf00d')

        group
            .append('path')
            .attr('class', 'marker')
            .attr('d', function(marker) {
                let position = scales.x(marker.start + (marker.end - marker.start) / 2)

                return 'M ' + position + ' 4 L ' + (position + 3) + ' 0 L ' + (position - 3) + ' 0 Z'
            })

        group = group.merge(marker)

        // Update existing nodes
        group
            .select('rect.bg')
            .transition()
            .attr('x', function(marker) {
                return scales.x(marker.start)
            })
            .attr('width', function(marker) {
                return scales.x(marker.end) - scales.x(marker.start)
            })

        group
            .select('g.button')
            .transition()
            .attr('transform', function(marker) {
                return 'translate(' + (scales.x(marker.end) - 16 - 4) + ', 4)'
            })

        group
            .select('path.marker')
            .transition()
            .attr('d', function(marker) {
                let position = scales.x(marker.start + (marker.end - marker.start) / 2)

                return 'M ' + position + ' -1 L ' + (position + 4) + ' -5 L ' + (position - 4) + ' -5 Z'
            })

        // Exiting node
        marker.exit().remove()
    }

    var _drawData = function(scales, lines) {
        const errorPathData = function(data) {
            const points = []

            data.forEach((point, index) => {
                points.push(point)

                const nextPoint = data[index + 1]
                if (!nextPoint) {
                    return
                }

                // Keep the full bucket width before a zero-value gap.
                if (point.value > 0 && !(nextPoint.value > 0)) {
                    points.push({
                        ...nextPoint,
                        value: point.value,
                    })
                }
            })

            return points
        }

        const extendedLines = []
        lines.forEach(line => {
            extendedLines.push(line)
            if (line.name === 'response') {
                extendedLines.push({
                    ...line,
                    name: 'large-response',
                    data: line.data.map(dataPoint => ({ ...dataPoint, value: dataPoint.value > 60000 ? dataPoint.value : null })),
                })
            }
        })

        let root = d3.select(svg).select('.lines')

        root.attr('width', dimensions.width - margin.left - margin.right)
            .attr('height', dimensions.height - margin.top - margin.bottom)
            .attr('transform', 'translate(' + margin.left + ', ' + margin.top + ')')

        // experiment
        //root
        //.transition()
        //.duration(750)
        //.attr("y", function(d) { return scales.y(d.value); })
        //.attr("height", function(d) { return dimensions.height - scales.y(d.value); });

        let pathFunction = d3
            .line()
            .x(function(point) {
                return scales.x(point.date)
            })
            .y(function(point) {
                return scales[point.axis](point.value)
            })
            .curve(d3.curveStepAfter)
        let pathFunctionRequests = d3
            .line()
            .x(function(point) {
                return scales.x(point.date)
            })
            .y(function(point) {
                return scales[point.axis](point.value)
            })
            .curve(d3.curveStepAfter)
        let pathFunctionErrors = d3
            .line()
            .defined(function(point) {
                return point.value > 0
            })
            .x(function(point) {
                return scales.x(point.date)
            })
            .y(function(point) {
                return scales[point.axis](point.value)
            })
            .curve(d3.curveStepAfter)
        let areaFunction = d3
            .area()
            .x(function(point) {
                return scales.x(point.date)
            })
            .y0(dimensions.height - margin.top - margin.bottom)
            .y1(function(point) {
                return scales[point.axis](point.value)
            })
            .curve(d3.curveStepAfter)

        let line = root.selectAll('g.line').data(extendedLines, function(line) {
            return line.name
        })

        // New data
        let newLine = line.enter()
        let group = newLine.append('g').attr('class', function(line) {
            return 'line ' + line.name
        })

        group
            .append('path')
            .attr('class', 'line')
            .attr('d', function(line) {
                if (line.name === 'requests') {
                    return pathFunctionRequests(line.data)
                }

                if (line.name === 'errors') {
                    return pathFunctionErrors(errorPathData(line.data))
                }

                return pathFunction(line.data)
            })
        group
            .append('path')
            .attr('class', 'fill')
            .attr('d', function(line) {
                return areaFunction(line.data)
            })

        // Update existing nodes
        line.select('path.line')
            .attr('d', function(line) {
                if (line.name === 'requests') {
                    return pathFunctionRequests(line.data)
                }

                if (line.name === 'errors') {
                    return pathFunctionErrors(errorPathData(line.data))
                }

                return pathFunction(line.data)
            })
            .attr('transform', null)
            .transition()
            .attr('transition', 'translate(' + scales.x(-1) + ')')
        line.select('path.fill')
            .attr('d', function(line) {
                return areaFunction(line.data)
            })
            .attr('transform', null)
            .transition()
            .attr('transition', 'translate(' + scales.x(-1) + ')')

        // Exiting node
        line.exit().remove()
    }

    var _drawEvents = function(scales, groups) {
        let root = d3.select(svg).selectAll('.events')
        let tooltip = d3.select(svg).select('.eventInfoBox')

        root.attr('width', dimensions.width - margin.left - margin.right)
            .attr('height', dimensions.height - margin.top - margin.bottom)
            .attr('transform', 'translate(' + margin.left + ', ' + margin.top + ')')

        let event = root.selectAll('g.event').data(groups, function(group) {
            return group[0].id
        })

        // New data
        let newevent = event.enter()
        let group = newevent
            .append('g')
            .attr('class', 'event')
            .on('mouseover', function() {
                const infoBox = document.querySelector('.infoBoxHtml')
                if (infoBox) {
                    infoBox.style.display = 'none'
                }

                tooltip.style('visibility', null)
            })
            .on('mouseout', function() {
                const infoBox = document.querySelector('.infoBoxHtml')
                if (infoBox) {
                    infoBox.style.display = 'block'
                }

                tooltip.style('visibility', 'hidden')
            })
            .on('mousemove', function(group) {
                _updateEventInfoBox(tooltip, d3.mouse(this), group)
            })

        group
            .append('rect')
            .attr('class', 'marker')
            .attr('y', 0.5)
            .attr('width', 1)
            .attr('height', dimensions.height - margin.top - margin.bottom)

        group = group.merge(event)

        // Update existing nodes
        group
            .select('rect.marker')
            .transition()
            .attr('x', function(group) {
                return scales.x(group[0].aggregateDate)
            })

        var eventRoot = group.selectAll('.flags').data(
            function(group) {
                return _.map(group, function(event) {
                    event.group = group
                    event.count = group.length

                    return event
                }).slice(0, 40)
            },
            function(event) {
                return 'e_' + event.id
            },
        )
        _drawEventFlags(eventRoot, scales)

        // Exiting node
        event.exit().remove()
    }

    var _drawEventFlags = function(root, scales) {
        var newEvent = root.enter()
        var group = newEvent
            .append('g')
            .attr('class', 'flags')
            .on('click', function(event) {
                d3.event.stopPropagation()
                jQuery(svg).trigger('eventClicked', [event])
            })

        // New node
        group
            .append('rect')
            .attr('class', 'flag')
            .attr('width', eventFlag.width)
            .attr('height', eventFlag.height)

        group.append('text').attr('class', 'icon')

        // merge existing and new nodes before continuing
        group = group.merge(root)
        group = _sortEventGroup(group, eventOrder.slice().reverse())

        group
            .select('rect.flag')
            .transition()
            .attr('class', function(event) {
                return 'flag ' + event.type
            })
            .attr('x', function(event) {
                return scales.x(event.aggregateDate) + 0.5
            })
            .attr('y', function(event, i) {
                return (event.count - i - 1) * 3 + 0.5
            })

        group
            .select('text.icon')
            .transition()
            .attr('x', function(event) {
                return scales.x(event.aggregateDate) + eventFlag.width / 2 + 0.5
            })
            .attr('y', function(event, i) {
                return (event.count - i - 1) * 3 + 0.5 + eventFlag.height * 0.8
            })
            .text(function(event) {
                return _getEventDisplayProperties(event.type).icon
            })

        // Exiting node
        root.exit().remove()
    }

    var _groupEvents = function(events, dates) {
        let bisect = d3.bisector(function(d) {
            return d
        }).right
        let groups = {}

        events = _.map(events, function(event) {
            event.aggregateDate = dates[bisect(dates, event.eventDate) - 1]

            if (!groups[event.aggregateDate]) {
                groups[event.aggregateDate] = [event]
            } else if (groups[event.aggregateDate].length < eventGroupLimit) {
                groups[event.aggregateDate].push(event)
            }
        })

        return _.values(groups)
    }

    var _sortEventGroup = function(group, order) {
        return group.sort(function(firstElement, secondElement) {
            return order.indexOf(firstElement.type) - order.indexOf(secondElement.type)
        })
    }

    var _drawEventInfoBox = function(container) {
        container
            .append('g')
            .attr('class', 'eventInfoBox')
            .style('visibility', 'hidden')
    }

    var _updateEventInfoBox = function(box, mouse, group) {
        const eventBoxDimensions = {
            defaultWidth: 200,
            marginX: 5,
            marginY: 2,
            iconWidth: 20,
            mouseOffset: 2,
        }

        box.selectAll('*').remove()

        let background = box.append('rect')
        let position = {
            x: mouse[0] + eventBoxDimensions.mouseOffset + margin.left,
            y: mouse[1] + eventBoxDimensions.mouseOffset + 10 + margin.top,
        }
        if (position.x + eventBoxDimensions.defaultWidth + eventBoxDimensions.mouseOffset > dimensions.width) {
            position.x = position.x - 2 * eventBoxDimensions.mouseOffset - eventBoxDimensions.defaultWidth
        }

        background
            .attr('x', position.x)
            .attr('y', position.y)
            .attr('width', eventBoxDimensions.defaultWidth)
            .attr('height', 2 * eventBoxDimensions.marginY + (group.length + 1) * 20)

        box.append('text')
            .attr('class', 'time')
            .attr('x', position.x + eventBoxDimensions.marginX)
            .attr('y', position.y + eventBoxDimensions.marginY + 16)
            .text(moment(group[0].eventDate).format('DD.MM.YYYY HH:mm'))

        let maxWidth = 200
        group = _sortEventGroup(group, eventOrder)
        for (let i = 0; i < group.length; ++i) {
            let event = group[i]

            box.append('text')
                .attr('class', 'icon ' + event.type)
                .attr('x', position.x + eventBoxDimensions.marginX)
                .attr('y', position.y + eventBoxDimensions.marginY + 16 + 20 * (i + 1))
                .text(_getEventDisplayProperties(event.type).icon)

            let text = box
                .append('text')
                .attr('class', 'event ' + event.type)
                .attr('x', position.x + eventBoxDimensions.marginX + eventBoxDimensions.iconWidth)
                .attr('y', position.y + eventBoxDimensions.marginY + 16 + 20 * (i + 1))
                .text(_getEventDisplayProperties(event.type).label + ': ' + event.shortName)

            const bbox = text.node().getBBox()
            if (bbox.width > maxWidth) {
                maxWidth = bbox.width
            }
        }

        maxWidth += 2 * eventBoxDimensions.marginX + eventBoxDimensions.iconWidth
        if (maxWidth > eventBoxDimensions.defaultWidth) {
            box.attr('width', maxWidth)
            background.attr('width', maxWidth)

            if (position.x + maxWidth + eventBoxDimensions.mouseOffset > dimensions.width) {
                const moveLeft = Math.round(-1 * (maxWidth - 2 * eventBoxDimensions.mouseOffset))
                box.selectAll('*').attr('transform', `translate(${moveLeft},0)`)
            }
        }
    }

    var _drawResponseTimeTarget = function(scales, targetValue) {
        const root = d3.select(svg).select('.targetMarkers')
        const [xRangeMin, xRangeMax] = scales.x.range()

        root.attr('width', dimensions.width - margin.left - margin.right)
            .attr('height', dimensions.height - margin.top - margin.bottom)
            .attr('transform', 'translate(' + margin.left + ', ' + margin.top + ')')

        let targetMarkers = root.selectAll('g.targetMarker').data(targetValue ? [targetValue] : [])

        // New data
        let newTargetMarkers = targetMarkers.enter()

        const newTargetMarkerGroups = newTargetMarkers.append('g').attr('class', 'targetMarker')

        newTargetMarkerGroups.append('line').attr('class', 'targetMarkerLine')

        newTargetMarkerGroups.append('text').attr('class', 'targetMarkerLabel')

        const currentTargetMarkers = newTargetMarkerGroups.merge(targetMarkers)

        // Update existing nodes
        currentTargetMarkers
            .select('line.targetMarkerLine')
            .transition()
            .attr('x1', xRangeMin)
            .attr('x2', xRangeMax)
            .attr('y1', scales.response(targetValue))
            .attr('y2', scales.response(targetValue))

        currentTargetMarkers
            .select('text.targetMarkerLabel')
            .transition()
            .attr('x', xRangeMin + 5)
            .attr('y', () => {
                let textLabelYPosition = scales.response(targetValue) + 12

                // If there is enough space below the marker line, draw text label underneath
                if (dimensions.height - margin.top - margin.bottom - textLabelYPosition >= 20) {
                    return textLabelYPosition
                }

                // else go above
                return scales.response(targetValue) - 4
            })
            .text(`Response time target: ${formatMilliseconds(targetValue)}`)

        // Exiting node
        targetMarkers.exit().remove()
    }

    var _getEventDisplayProperties = function(type) {
        if (!eventMap[type]) {
            return { label: type, icon: '\uf0f3' }
        }

        return eventMap[type]
    }

    var appendValue = function(dataSet) {
        let previous = dataSet.data[dataSet.data.length - 2]
        let current = dataSet.data[dataSet.data.length - 1]

        if (!previous) {
            return dataSet
        }

        let next = _.clone(current)
        let difference = moment.duration(moment(current.date).diff(moment(previous.date)))

        next.date = moment(next.date)
            .add(difference)
            .toDate()

        dataSet.data[dataSet.data.length] = next
        return dataSet
    }

    this.update = function(data, events, rangeMarkers, hasDownstream = false, responseTimeTarget = null, yAxisMax = null) {
        drawXAxisLabels = !hasDownstream

        lines = _.map(data, appendValue)

        scales = _scales(lines, yAxisMax)
        let groups = _groupEvents(events, _.pluck(lines[1].data, 'date'))

        _drawAxis(scales, _axis(scales))
        _drawRangeMarkers(scales, rangeMarkers)
        _drawData(scales, lines)
        _drawEvents(scales, groups)
        _drawResponseTimeTarget(scales, responseTimeTarget)
    }

    this.destroy = function() {
        // remove all children from the svg.
        d3.select(svg)
            .selectAll('*')
            .remove()
    }
}

export default Chart
